import React from 'react';

import { Messages } from '../../../../../gen-src/Localize';
import {
  FlowUpstreamVirtualConnectionTablesQueryVariables, FlowUpstreamVirtualConnectionTablesQuery, VirtualConnectionTablesDisplayGridRowDataFragment, FlowUpstreamVirtualConnectionTablesFilteredByFlowOutputFieldQueryVariables, FlowUpstreamVirtualConnectionTablesFilteredByFlowOutputFieldQuery, VirtualConnectionTableSortOrder,
  FlowUpstreamVirtualConnectionTablesFilteredByFlowOutputFieldDocument,
  FlowUpstreamVirtualConnectionTablesDocument
} from '../../../../../gen-src/queries';
import {
  ConnectionListResult,
  GridSortProps,
  MaxCountsResultData,
  PaginatedQueryWrapper,
} from '../../../data-catalog-shared';
import { VirtualConnectionTablesDisplayGrid } from '../sharedDisplayGrid/VirtualConnectionTablesDisplayGrid';
import {
  VirtualConnectionTablesValidSortFields,
} from '../sorting/sortProviders/VirtualConnectionTablesGridSortProvider';
import { getRowDataWithMaxCountsForVirtualConnectionTable } from '../table/functions/VirtualConnectionTableRowDataFunctions';
import { VirtualConnectionTablesDisplayGridRowData } from '../table/types/VirtualConnectionTablesDisplayGridRowData';

type QueryVariables = FlowUpstreamVirtualConnectionTablesQueryVariables;
type QueryResults = FlowUpstreamVirtualConnectionTablesQuery;
type QueryResultListNode = VirtualConnectionTablesDisplayGridRowDataFragment;
type GridRowData = VirtualConnectionTablesDisplayGridRowData;

type QueryVariablesWithFilter = FlowUpstreamVirtualConnectionTablesFilteredByFlowOutputFieldQueryVariables;
type QueryResultsWithFilter = FlowUpstreamVirtualConnectionTablesFilteredByFlowOutputFieldQuery;
type QueryResultListNodeWithFilter = VirtualConnectionTablesDisplayGridRowDataFragment;

function selectConnectionListResult(resultData: QueryResults): ConnectionListResult<QueryResultListNode> | null {
  const flow = resultData && resultData.flows && resultData.flows[0];
  return flow ? flow.upstreamVirtualConnectionTablesConnection : null;
}

function selectConnectionListResultForFilteredQuery(
  resultData: QueryResultsWithFilter
): ConnectionListResult<QueryResultListNodeWithFilter> | null {
  const flow = resultData && resultData.flows && resultData.flows[0];
  const flowOutputFields = flow && flow.outputFieldsConnection && flow.outputFieldsConnection.nodes;
  const field = flowOutputFields && flowOutputFields[0];
  return field ? field.upstreamVirtualConnectionTablesConnection : null;
}

function selectMaxCountsResult(resultData: QueryResults): MaxCountsResultData | null {
  const flow = resultData && resultData.flows && resultData.flows[0];
  return flow;
}

function selectMaxCountsResultForFilteredQuery(resultData: QueryResultsWithFilter): MaxCountsResultData | null {
  const flow = resultData && resultData.flows && resultData.flows[0];
  const flowOutputFields = flow && flow.outputFieldsConnection && flow.outputFieldsConnection.nodes;
  const field = flowOutputFields && flowOutputFields[0];
  return field;
}

function getRowDataForVirtualConnectionTable(
  table: VirtualConnectionTablesDisplayGridRowDataFragment,
  resultData: QueryResults,
): VirtualConnectionTablesDisplayGridRowData {
  return getRowDataWithMaxCountsForVirtualConnectionTable(table, selectMaxCountsResult(resultData));
}

function getRowDataForVirtualConnectionTableForFilteredQuery(
  table: VirtualConnectionTablesDisplayGridRowDataFragment,
  resultData: QueryResultsWithFilter,
): VirtualConnectionTablesDisplayGridRowData {
  return getRowDataWithMaxCountsForVirtualConnectionTable(table, selectMaxCountsResultForFilteredQuery(resultData));
}

export type FlowUpstreamVirtualConnectionTablesQueryGridProps =
  GridSortProps<VirtualConnectionTablesValidSortFields> & {
    readonly flowVizportalUrlId: string;
    readonly filteredFlowOutputFieldId: string | null;
    readonly rowCount: number | null;
  };

export function FlowUpstreamVirtualConnectionTablesQueryGrid(props: FlowUpstreamVirtualConnectionTablesQueryGridProps): JSX.Element {
  const emptyGridMessage = Messages.EmptyUpstreamVirtualConnectionTablesLineageGridMessage();
  const emptyFilteredGridMessage = Messages.EmptyUpstreamFilteredVirtualConnectionTablesLineageGridMessage();

  return props.filteredFlowOutputFieldId ? (
    // Query with Selected Field as Filter
    <PaginatedQueryWrapper<QueryVariablesWithFilter, QueryResultsWithFilter, QueryResultListNode, GridRowData>
      queryGql={FlowUpstreamVirtualConnectionTablesFilteredByFlowOutputFieldDocument}
      partialQueryVariables={{
        flowVizportalUrlId: props.flowVizportalUrlId,
        selectedFlowOutputFieldId: props.filteredFlowOutputFieldId,
        orderBy: props.orderBy as VirtualConnectionTableSortOrder,
      }}
      selectConnectionListResult={selectConnectionListResultForFilteredQuery}
      mapResultNode={getRowDataForVirtualConnectionTableForFilteredQuery}
    >
      {({ rowStore }) =>
        <VirtualConnectionTablesDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyFilteredGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />
      }
    </PaginatedQueryWrapper>
  ) : (
    // Query without Selection
    <PaginatedQueryWrapper<QueryVariables, QueryResults, QueryResultListNode, GridRowData>
      queryGql={FlowUpstreamVirtualConnectionTablesDocument}
      partialQueryVariables={{
        flowVizportalUrlId: props.flowVizportalUrlId,
        orderBy: props.orderBy as VirtualConnectionTableSortOrder,
      }}
      selectConnectionListResult={selectConnectionListResult}
      mapResultNode={getRowDataForVirtualConnectionTable}
    >
      {({ rowStore }) =>
        <VirtualConnectionTablesDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />}
    </PaginatedQueryWrapper>
  );
}
